<section class="page" aria-labelledby="workers-title">
  <header class="hero">
    <p class="eyebrow">System</p>
    <h1 id="workers-title">Workers</h1>
    <p class="lede">
      Live status for background schedulers, recent activity, and guided recovery actions.
      <span class="pulse" aria-hidden="true"></span>
      Updates every 2.5s
    </p>
  </header>

  @if (errorMessage) {
    <p class="error" role="alert">{{ errorMessage }}</p>
  }
  @if (actionNote) {
    <p class="ok" role="status">{{ actionNote }}</p>
  }

  @if (loading && !status) {
    <p class="muted">Loading workers…</p>
  } @else if (status) {
    <section class="fleet" aria-label="Worker fleet">
      @for (worker of status.workers; track trackWorker($index, worker)) {
        <article class="worker-card" [attr.data-state]="worker.state">
          <div class="worker-top">
            <span class="pip" [attr.data-state]="worker.state" aria-hidden="true"></span>
            <div class="worker-title">
              <h2>{{ worker.name }}</h2>
              <p class="state">{{ stateLabel(worker.state) }}</p>
            </div>
          </div>

          <dl class="meta">
            <div>
              <dt>Last tick</dt>
              <dd>{{ relativeTime(worker.lastTickAt) }}</dd>
            </div>
            @if (worker.phase) {
              <div>
                <dt>Phase</dt>
                <dd class="bidi" dir="auto">{{ worker.phase }}</dd>
              </div>
            }
            @if (worker.backlog != null && worker.backlog > 0) {
              <div>
                <dt>Backlog</dt>
                <dd class="mono">{{ worker.backlog | number }}</dd>
              </div>
            }
          </dl>

          @if (worker.lastError) {
            <p class="worker-error bidi" dir="auto">{{ worker.lastError }}</p>
          }

          @if (showRecent(worker)) {
            <div class="recent" aria-label="Recent activity">
              <p class="recent-label">Recent</p>
              @if (worker.recent.length === 0) {
                <p class="recent-empty muted">No ticks yet</p>
              } @else {
                <ul class="recent-list" [class.recent-list-rich]="true">
                  @for (item of worker.recent; track item.id; let i = $index) {
                    @if (workerBatch(item); as batch) {
                      <li class="recent-rich" [attr.data-severity]="item.severity">
                        <div class="recent-rich-head">
                          <time class="recent-time mono" [attr.datetime]="item.at">{{
                            shortDateTime(item.at)
                          }}</time>
                          <div class="stat-chips" aria-label="Batch counts">
                            @for (stat of batch.stats; track trackStat($index, stat)) {
                              <span class="stat-chip" [attr.data-tone]="stat.tone">{{
                                stat.count
                              }}
                              {{ stat.label }}</span>
                            }
                          </div>
                          @if (batch.hasSamples) {
                            <button
                              type="button"
                              class="linkish recent-toggle"
                              (click)="toggleBatchRecent(worker, item, i)"
                              [attr.aria-expanded]="isBatchRecentExpanded(worker, item, i)"
                            >
                              {{
                                isBatchRecentExpanded(worker, item, i)
                                  ? 'Hide samples'
                                  : 'Show samples'
                              }}
                            </button>
                          }
                        </div>
                        @if (batch.hasSamples && isBatchRecentExpanded(worker, item, i)) {
                          <div class="sample-groups">
                            @for (group of batch.groups; track trackGroup($index, group)) {
                              <div class="sample-group" [attr.data-tone]="group.tone">
                                <p class="sample-label">
                                  {{ group.label }}
                                  @if (moreCount(group.total, group.samples.length) > 0) {
                                    <span class="muted"
                                      >· top {{ group.samples.length }} of {{ group.total }}</span
                                    >
                                  }
                                </p>
                                <ul class="sample-list">
                                  @for (sample of group.samples; track trackSample($index, sample)) {
                                    <li>
                                      <span class="sample-name bidi" dir="auto">{{
                                        sampleLabel(sample)
                                      }}</span>
                                      @if (sampleMeta(sample)) {
                                        <span class="sample-meta mono">{{ sampleMeta(sample) }}</span>
                                      }
                                      @if (sampleUrl(sample); as href) {
                                        <span class="sample-meta sample-url mono">{{ href }}</span>
                                      }
                                    </li>
                                  }
                                </ul>
                              </div>
                            }
                          </div>
                        }
                      </li>
                    } @else {
                      <li [attr.data-severity]="item.severity">
                        <time class="recent-time mono" [attr.datetime]="item.at">{{
                          shortDateTime(item.at)
                        }}</time>
                        <span class="recent-msg bidi" dir="auto">{{ item.message }}</span>
                      </li>
                    }
                  }
                </ul>
              }
            </div>
          }

          @if (worker.action) {
            <button
              type="button"
              class="btn"
              [disabled]="busyWorkerId === worker.id"
              (click)="runAction(worker.action, worker.id)"
            >
              {{ busyWorkerId === worker.id ? 'Running…' : 'Run now' }}
            </button>
          }
        </article>
      }
    </section>

    @if (status.checkpoints.length > 0) {
      <section class="checkpoints card" aria-label="Ingest checkpoints">
        <h2>Ingest checkpoints</h2>
        <ul class="checkpoint-list">
          @for (cp of status.checkpoints; track cp.chainExternalCode) {
            <li>
              <span class="bidi" dir="auto">{{ cp.chainName }}</span>
              <span class="mono">{{ cp.mode }} · {{ cp.status }}</span>
              @if (cp.ageSeconds != null) {
                <span class="muted">updated {{ relativeTime(cp.updatedAt) }}</span>
              }
            </li>
          }
        </ul>
      </section>
    }

    <div class="split">
      <section class="panel feed-panel" aria-label="Live feed">
        <div class="panel-head">
          <h2>Live feed</h2>
          <div class="filters" role="group" aria-label="Feed filters">
            <button
              type="button"
              class="chip"
              [class.active]="feedFilter === 'all'"
              (click)="setFeedFilter('all')"
            >
              All
            </button>
            <button
              type="button"
              class="chip"
              [class.active]="feedFilter === 'errors'"
              (click)="setFeedFilter('errors')"
            >
              Errors
            </button>
            @for (worker of status.workers; track worker.id) {
              <button
                type="button"
                class="chip"
                [class.active]="feedFilter === worker.id"
                (click)="setFeedFilter(worker.id)"
              >
                {{ worker.name }}
              </button>
            }
          </div>
        </div>

        @if (filteredFeed.length === 0) {
          <div class="empty">
            <p class="empty-title">Quiet</p>
            <p class="muted">No feed items for this filter yet.</p>
          </div>
        } @else {
          <ul class="feed">
            @for (item of filteredFeed; track trackFeed($index, item)) {
              <li class="feed-item" [attr.data-severity]="item.severity">
                <div class="feed-row">
                  <span class="sev">{{ item.severity }}</span>
                  <span class="worker-tag">{{ item.workerId }}</span>
                  <time class="mono" [attr.datetime]="item.at">{{ relativeTime(item.at) }}</time>
                </div>
                <p class="feed-msg bidi" dir="auto">{{ item.message }}</p>
                @if (workerBatch(item); as batch) {
                  <div class="stat-chips feed-chips" aria-label="Batch counts">
                    @for (stat of batch.stats; track trackStat($index, stat)) {
                      <span class="stat-chip" [attr.data-tone]="stat.tone"
                        >{{ stat.count }} {{ stat.label }}</span
                      >
                    }
                  </div>
                  @if (batch.hasSamples) {
                    <div class="sample-groups feed-samples">
                      @for (group of batch.groups; track trackGroup($index, group)) {
                        <div class="sample-group" [attr.data-tone]="group.tone">
                          <p class="sample-label">{{ group.label }}</p>
                          <ul class="sample-list">
                            @for (sample of group.samples; track trackSample($index, sample)) {
                              <li>
                                <span class="sample-name bidi" dir="auto">{{
                                  sampleLabel(sample)
                                }}</span>
                                @if (sampleMeta(sample)) {
                                  <span class="sample-meta mono">{{ sampleMeta(sample) }}</span>
                                }
                                @if (sampleUrl(sample); as href) {
                                  <span class="sample-meta sample-url mono">{{ href }}</span>
                                }
                              </li>
                            }
                          </ul>
                        </div>
                      }
                    </div>
                  }
                } @else {
                  @if (item.errorCode) {
                    <p class="mono code">{{ item.errorCode }}</p>
                  }
                  @if (item.detail) {
                    <button type="button" class="linkish" (click)="toggleFeedDetail(item)">
                      {{ expandedFeedId === feedKey(item) ? 'Hide detail' : 'Show detail' }}
                    </button>
                    @if (expandedFeedId === feedKey(item)) {
                      <pre class="detail bidi" dir="auto">{{ item.detail }}</pre>
                    }
                  }
                }
              </li>
            }
          </ul>
        }
      </section>

      <section class="panel issues-panel" aria-label="Actionable issues">
        <div class="panel-head">
          <h2>Actionable issues</h2>
          @if (issues && issues.pendingUnitReviews > 0) {
            <a class="chip link" routerLink="/admin/unit-reviews">
              {{ issues.pendingUnitReviews }} unit reviews
            </a>
          }
        </div>

        @if (!issues || (issues.issues.length === 0 && healthy)) {
          <div class="empty healthy">
            <p class="empty-title">All clear</p>
            <p class="muted">No open error codes or scheduler incidents in the last 24 hours.</p>
          </div>
        } @else {
          <ul class="issues">
            @for (issue of issues.issues; track trackIssue($index, issue)) {
              <li class="issue-card" [attr.data-severity]="issue.severity">
                <div class="issue-top">
                  <span class="sev">{{ issue.severity }}</span>
                  <span class="mono">{{ issue.code }}</span>
                  <span class="count">×{{ issue.count }}</span>
                </div>
                <h3 class="bidi" dir="auto">{{ issue.title }}</h3>
                <p class="label">Analysis</p>
                <p class="bidi" dir="auto">{{ issue.analysis }}</p>
                <p class="label">Next</p>
                <p class="next bidi" dir="auto">{{ issue.nextStep }}</p>
                @if (issue.action) {
                  <button
                    type="button"
                    class="btn"
                    [disabled]="busyIssueCode === issue.code"
                    (click)="runAction(issue.action, undefined, issue.code)"
                  >
                    {{ busyIssueCode === issue.code ? 'Running…' : issue.action.label }}
                  </button>
                }
              </li>
            }
          </ul>
        }
      </section>
    </div>
  }
</section>
